import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import Loading from 'views/App/Portal/Loading';
import { Box, GlobalStyles, Stack, Typography } from '@mui/material';
import { ToolbarLeft, ToolbarRight } from './_components/Toolbar';
import { useReviewUpdatesContext } from './context';
import ReviewDrawerUpdates from './_components/ReviewDrawerUpdates';
import ReviewSection from './_components/ReviewSection';
import { neutral } from '_lex/_foundations/phxColors';
import { translate as t } from 'hooks/useTranslations';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import draftManualHelper from '../../helpers/draftManualHelper';
import { DraftPolicySubSection } from 'views/App/Portal/Policy/_types/manuals/draft';
import SectionTags from '../PolicyEditor/SectionTags';
import { ManualFilterOptions } from 'views/App/Portal/Policy/_types/enums';
import AttachmentBox from '../AttachmentLibrary/_components/AttachmentBox';
import parse, { DOMNode } from 'html-react-parser';
import contentConversionHelper from '../../helpers/contentConversionHelper';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import { ContentPage } from '../styles';
import useManagePolicy from '../../_data_sources/useManagePolicy';
import { usePolicyEditorContext } from '../PolicyEditor/context';
import { useQueryClient } from 'react-query';
import { ServerErrorResponse } from 'data_sources/constants';
import errorHelper from 'helpers/errorHelper';
import { AppContext } from 'views/App/context';
import { enqueueSnackbar } from 'notistack';

const ReviewUpdates: React.FC = () => {
  const { id: manualId, content: contentParam } = useParams<{
    id: string;
    content: string;
  }>();
  const {
    focus,
    handleDone,
    manual,
    policyId,
    prefaceId,
    policyUpdates,
    prefaceUpdates,
    policyQuery,
    policyData,
    prefaceQuery,
    prefaceData,
    isRightDrawerOpen,
    sidebarItems,
    setIsRightDrawerOpen,
  } = useReviewUpdatesContext();
  const { currentProfile } = useContext(AppContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { getLock } = useManagePolicy();
  const queryClient = useQueryClient();
  const previousPath = (location.state as { from?: string })?.from;
  const [policyNumber, setPolicyNumber] = useState<string>('');
  const [gettingLock, setGettingLock] = useState<boolean>(false);

  usePolicyEditorContext();

  const hasHandledDashboardEntryRef = useRef(false);

  useEffect(() => {
    if (policyData && manual) {
      setPolicyNumber(draftManualHelper.getNumberManualNode(policyData, manual));
    }
  }, [policyData, manual]);

  useEffect(() => {
    const isFromDashboard = previousPath?.includes('/admin/dashboard');
    if (!isFromDashboard || !manual?.id || !policyId) return;
    if (!policyData) return;

    if (hasHandledDashboardEntryRef.current) return;
    hasHandledDashboardEntryRef.current = true;

    if(policyData.isNew) {
      navigate(`../${manualId}#policy:${policyId}`);
      return;
    }

    const isLockedByOther =
      policyData.isLocked && policyData.lock?.userId !== currentProfile?.user?.id;

      if (isLockedByOther) {
      enqueueSnackbar(t('draftManuals.reviewUpdates.policyLocked', '', { title: policyData.title }), {
        variant: 'error',
      });
      navigate(`../${manualId}#policy:${policyId}`);
      return;
    }

    if (policyData.isLocked) return;

    let aborted = false;
    setGettingLock(true);

    const acquireLock = async () => {
      try {
        await new Promise<void>((resolve, reject) => {
          getLock(
            manual.id,
            policyData.id,
            () => {
              if (aborted) return;
              queryClient.invalidateQueries(['draft_manuals']);
              queryClient.invalidateQueries(['draft_manual', manual.id]);
              queryClient.invalidateQueries(['draft_policy', manual.id, policyData.id]);
              resolve();
            },
            (error: ServerErrorResponse) => {
              if (aborted) return;
              errorHelper.enqueueErrors(error);
              navigate(`../${manualId}/policy:${policyId}`);
              reject();
            }
          );
        });
      } catch {
        // Error already handled in onError callback
      } finally {
        setGettingLock(false);
      }
    };

    (async () => {
      await acquireLock();
    })();

    return () => {
      aborted = true;
    };
  }, [
    previousPath,
    manual?.id,
    policyId,
    policyData,
    currentProfile?.user?.id,
    navigate,
    manualId,
    getLock,
    queryClient,
    enqueueSnackbar,
    t,
  ]);

  const rightDrawerProps = isRightDrawerOpen
    ? {
      header: (
        <Box
          sx={{
            display: 'flex',
            height: '64px',

            justifyContent: 'start',
            gap: 6,
            alignItems: 'center',
            p: 3,
          }}
        >
          <Typography variant='h2'>{t('draftManuals.reviewUpdates.title')}</Typography>
        </Box>
      ),
      content: <ReviewDrawerUpdates key={`drawer_${policyId || prefaceId}`} />,
      open: isRightDrawerOpen,
      onClose: () => setIsRightDrawerOpen(false),
    }
    : undefined;

  const filteredSections = useCallback(() => {
    return policyData?.sections.filter((section) => section.state === ManualFilterOptions.ENABLED || section.isNew);
  }, [policyData]);

  const filterSubsections = useCallback((subSections: DraftPolicySubSection[]) => {
    return subSections.filter((sub) => sub.state === ManualFilterOptions.ENABLED || sub.isNew);
  }, []);
  const currentItem = sidebarItems.find((x) => x.id === focus);

  return (
    <>
      <GlobalStyles
        styles={{
          ins: {
            textDecoration: 'underline double',
            color: '#008000',
          },
          del: {
            color: '#cc0000',
            textDecoration: 'line-through',
          },
        }}
      />
      <TakeoverLayout
        pageTitle={`${t('draftManuals.reviewUpdates.title')}: ${manual?.name || ''}`}
        contentHeaderLeft={<ToolbarLeft />}
        contentHeaderRight={<ToolbarRight />}
        rightDrawer={rightDrawerProps}
        noPadding={true}
        onBackClick={() => handleDone()}
        backUrl={
          policyId
            ? `../${manualId}/policy:${policyId}`
            : prefaceId
              ? `../${manualId}/preface:${prefaceId}`
              : `../${manualId}`
        }
      >
        <Box>
          {policyId && manual && (
            <>
              {!!policyData && !gettingLock ? (
                <Box sx={{ padding: 6 }}>
                  <Stack spacing={4}>
                    {policyData && !gettingLock ? (
                      <>
                        <Stack sx={{ pb: 8 }}>
                          {currentItem?.id === 'policy-title' ? (
                            <ReviewSection key={policyUpdates?.titleChangesetId} />
                          ) : (
                            <Box>
                              <Typography variant='overline'>
                                {t('draftManuals.policyEditor.title')}{' '}
                                {draftManualHelper.getNumberManualNode(policyData, manual)}
                              </Typography>
                              <Typography
                                component='div'
                                variant='h5'
                              >
                                {draftManualHelper.formatManualNodeTitle(policyQuery.data, manual)}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                        <Box sx={{ minHeight: 0, overflow: 'auto' }}>
                          <Stack spacing={10}>
                            {filteredSections()?.map((section, sectionIndex) => (
                              <React.Fragment key={`section-${section.id}-${sectionIndex}`}>
                                {currentItem?.id === section.id ? (
                                  <ReviewSection key={`${section.id}_${sectionIndex}`} />
                                ) : (
                                  <Box key={`${section.id}_${sectionIndex}`}>
                                    <Box>
                                      <Stack spacing={1}>
                                        <SectionTags
                                          section={section}
                                          agencyModified={section.agencyModified}
                                          policyData={policyData}
                                          manual={manual}
                                        />
                                        <Typography
                                          className={`title_${section.id}`}
                                          component='div'
                                          variant='h6'
                                        >
                                          {draftManualHelper.formatPolicyNodeTitle(section, policyNumber, policyData)}
                                        </Typography>
                                        <Box sx={{ minHeight: 0, overflow: 'auto' }}>
                                          {parse(contentConversionHelper.contentForRender(section.content ?? ''), {
                                            replace(node: DOMNode, ind: number) {
                                              return PolicyContentHelper.handleContent(node, ind);
                                            },
                                          })}
                                        </Box>
                                        {section.attachments && section.attachments.length > 0 && (
                                          <Box sx={{ mt: 2, mb: 2 }}>
                                            <Stack
                                              direction='row'
                                              spacing={1}
                                            >
                                              {manual &&
                                                section.attachments.map((att: any) => (
                                                  <AttachmentBox
                                                    key={`attachment-${att.id}`}
                                                    attachmentRef={att}
                                                    manualId={manual?.id}
                                                    onRemove={() => { }}
                                                    isEditable={false}
                                                  />
                                                ))}
                                            </Stack>
                                          </Box>
                                        )}
                                      </Stack>
                                    </Box>
                                    <Stack
                                      spacing={10}
                                      sx={{ mt: 10 }}
                                    >
                                      {filterSubsections(section.subSections).map((subSection, subSectionIndex) => (
                                        <React.Fragment key={`subsection-${subSection.id}-${subSectionIndex}`}>
                                          {focus === subSection.id ? (
                                            <ReviewSection key={`${subSection.id}_${subSectionIndex}`} />
                                          ) : (
                                            <Box key={`${subSection.id}_${subSectionIndex}`}>
                                              <Box sx={{ ml: 5 }}>
                                                <Stack spacing={1}>
                                                  <SectionTags
                                                    section={subSection}
                                                    agencyModified={subSection.agencyModified}
                                                    policyData={policyData}
                                                    manual={manual}
                                                  />
                                                  <Typography
                                                    className={`title_${subSection.id}`}
                                                    component='div'
                                                    variant='h6'
                                                  >
                                                    {draftManualHelper.formatPolicyNodeTitle(
                                                      subSection,
                                                      policyNumber,
                                                      policyData
                                                    )}
                                                  </Typography>
                                                  <Box sx={{ minHeight: 0, overflow: 'auto' }}>
                                                    {parse(
                                                      contentConversionHelper.contentForRender(
                                                        subSection.content ?? ''
                                                      ),
                                                      {
                                                        replace(node: DOMNode, ind: number) {
                                                          return PolicyContentHelper.handleContent(node, ind);
                                                        },
                                                      }
                                                    )}
                                                  </Box>
                                                  {subSection.attachments && subSection.attachments.length > 0 && (
                                                    <Box sx={{ mt: 2 }}>
                                                      <Stack
                                                        direction='row'
                                                        spacing={1}
                                                      >
                                                        {subSection.attachments.map((att: any) => (
                                                          <AttachmentBox
                                                            key={`attachment-${att.id}`}
                                                            attachmentRef={att}
                                                            manualId={manual.id}
                                                            onRemove={(id) => { }}
                                                            isEditable={false}
                                                          />
                                                        ))}
                                                      </Stack>
                                                    </Box>
                                                  )}
                                                </Stack>
                                              </Box>
                                            </Box>
                                          )}
                                        </React.Fragment>
                                      ))}
                                    </Stack>
                                  </Box>
                                )}
                              </React.Fragment>
                            ))}
                          </Stack>
                        </Box>
                      </>
                    ) : null}
                  </Stack>
                </Box>
              ) : (
                <Loading />
              )}
            </>
          )}
          {prefaceId && manual && (
            <>
              {!!prefaceData ? (
                <ContentPage
                  sx={{ width: '100% !important' }}
                  className='phx-preface'
                >
                  <Stack spacing={4}>
                    {prefaceData ? (
                      <>
                        <Stack sx={{ pb: 8 }}>
                          {currentItem?.id === 'preface' ? (
                            <ReviewSection key={prefaceUpdates?.changesetId} />
                          ) : (
                            <Box>
                              <Typography variant='overline'>{t('draftManuals.prefaceEditor.title')}</Typography>
                              <Typography
                                component='div'
                                variant='h5'
                              >
                                {prefaceData.title}
                              </Typography>
                              <Box sx={{ minHeight: 0, overflow: 'auto' }}>
                                {parse(contentConversionHelper.contentForRender(prefaceData.content ?? ''), {
                                  replace(node: DOMNode, ind: number) {
                                    return PolicyContentHelper.handleContent(node, ind);
                                  },
                                })}
                              </Box>
                            </Box>
                          )}
                        </Stack>
                      </>
                    ) : null}
                  </Stack>
                </ContentPage>
              ) : (
                <Loading />
              )}
            </>
          )}
        </Box>
      </TakeoverLayout>
    </>
  );
};

export default ReviewUpdates;
